import { useEffect, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  Globe,
  Image,
  MessageCircle,
  MoreHorizontal,
  Pencil,
  Send,
  Smile,
  Trash2,
  X,
  ZoomIn,
} from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Textarea } from './ui/textarea';
import ImageLightbox from './ImageLightbox';
import type { NewsFeedComment, NewsFeedPost } from '../types';
import { generateId } from '../utils/idGenerator';

const FEED_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🎉'];

export interface NewsFeedComposerDraft {
  content: string;
  photos: string[];
}

interface NewsFeedComposerProps {
  currentUserName: string;
  currentUserAvatar?: string;
  fallbackAvatarSrc?: string;
  placeholder: string;
  onSubmit: (draft: NewsFeedComposerDraft) => Promise<void> | void;
  successMessage?: string;
  errorMessage?: string;
}

interface NewsFeedPostCardProps {
  post: NewsFeedPost;
  currentUserId: string;
  currentUserName: string;
  currentUserAvatar?: string;
  onReact: (post: NewsFeedPost, emoji: string) => Promise<void> | void;
  onComment: (post: NewsFeedPost, comment: NewsFeedComment) => Promise<void> | void;
  onDelete: (post: NewsFeedPost) => Promise<void> | void;
  onEdit: (post: NewsFeedPost, content: string) => Promise<void> | void;
}

interface ExpandableFeedTextareaProps
  extends Omit<React.ComponentProps<typeof Textarea>, 'value' | 'onChange'> {
  value: string;
  onValueChange: (value: string) => void;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  persistExpanded?: boolean;
  expandedRows?: number;
  collapsedClassName?: string;
  expandedClassName?: string;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function ExpandableFeedTextarea({
  value,
  onValueChange,
  expanded,
  onExpandedChange,
  persistExpanded = false,
  expandedRows = 3,
  collapsedClassName = 'h-10 !min-h-10',
  expandedClassName = '!min-h-24',
  className = '',
  onFocus,
  onBlur,
  ...props
}: ExpandableFeedTextareaProps) {
  const isExpanded = expanded || Boolean(value.trim());

  return (
    <Textarea
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
      onFocus={(event) => {
        onExpandedChange(true);
        onFocus?.(event);
      }}
      onBlur={(event) => {
        if (!value.trim() && !persistExpanded) {
          onExpandedChange(false);
        }
        onBlur?.(event);
      }}
      className={`resize-none overflow-hidden transition-[min-height,height] duration-200 ${
        isExpanded ? expandedClassName : collapsedClassName
      } ${className}`}
      rows={isExpanded ? expandedRows : 1}
      {...props}
    />
  );
}

function ReactionBar({
  reactions,
  currentUserId,
  onReact,
}: {
  reactions: Array<{ userId: string; emoji: string }>;
  currentUserId: string;
  onReact: (emoji: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const counts = reactions.reduce<Record<string, number>>((accumulator, reaction) => {
    accumulator[reaction.emoji] = (accumulator[reaction.emoji] ?? 0) + 1;
    return accumulator;
  }, {});
  const myEmoji = reactions.find((reaction) => reaction.userId === currentUserId)?.emoji;

  return (
    <div className="relative flex items-center gap-3">
      <div className="relative">
        <button
          onClick={() => setOpen((value) => !value)}
          className={`flex items-center gap-1 rounded-full px-3 py-1 text-sm transition-colors ${
            myEmoji
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:bg-muted'
          }`}
          aria-label="React to post"
        >
          <Smile className="h-4 w-4" />
          {myEmoji ? <span>{myEmoji}</span> : <span>React</span>}
        </button>
        {open && (
          <div className="absolute bottom-9 left-0 z-20 flex items-center gap-1 rounded-full border border-border bg-popover px-2 py-1 shadow-lg">
            {FEED_REACTIONS.map((emoji) => (
              <button
                key={emoji}
                className="text-xl transition-transform hover:scale-125"
                onClick={() => {
                  onReact(emoji);
                  setOpen(false);
                }}
                aria-label={`React with ${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>

      {Object.entries(counts).map(([emoji, count]) => (
        <span key={emoji} className="text-sm text-muted-foreground">
          {emoji} {count}
        </span>
      ))}
    </div>
  );
}

function FeedComment({ comment }: { comment: NewsFeedComment }) {
  return (
    <div className="mt-2 flex gap-2">
      <Avatar className="h-7 w-7 shrink-0">
        <AvatarImage src={comment.authorAvatar} />
        <AvatarFallback className="bg-muted text-xs text-foreground">
          {getInitials(comment.authorName)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 rounded-2xl bg-muted px-3 py-2">
        <p className="text-xs font-semibold text-foreground">{comment.authorName}</p>
        <p className="mt-0.5 text-sm text-foreground/90">{comment.content}</p>
      </div>
    </div>
  );
}

export function NewsFeedPostCard({
  post,
  currentUserId,
  currentUserName,
  currentUserAvatar,
  onReact,
  onComment,
  onDelete,
  onEdit,
}: NewsFeedPostCardProps) {
  const [showComments, setShowComments] = useState(false);
  const [commentInput, setCommentInput] = useState('');
  const [commentExpanded, setCommentExpanded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState(post.content);
  const [showMenu, setShowMenu] = useState(false);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const isOwn = post.authorId === currentUserId;
  const hasPhotos = Boolean(post.photos && post.photos.length > 0);

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  const handleComment = async () => {
    if (!commentInput.trim()) {
      return;
    }

    const comment: NewsFeedComment = {
      id: generateId('fcomment'),
      postId: post.id,
      authorId: currentUserId,
      authorName: currentUserName,
      authorAvatar: currentUserAvatar,
      content: commentInput.trim(),
      createdAt: new Date().toISOString(),
    };

    await onComment(post, comment);
    setCommentInput('');
    setCommentExpanded(false);
  };

  const handleDelete = async () => {
    if (!confirm('Delete this post?')) {
      return;
    }

    setShowMenu(false);

    try {
      await onDelete(post);
      toast.success('Post deleted');
    } catch (error) {
      console.error('[NewsFeedPostCard] Failed to delete post:', error);
      toast.error('Failed to delete post');
    }
  };

  const handleEdit = async () => {
    if (!editContent.trim()) {
      return;
    }

    await onEdit(post, editContent.trim());
    setEditMode(false);
    toast.success('Post updated');
  };

  return (
    <Card className="overflow-hidden border-border bg-card">
      <CardContent className="p-0">
        <div className="flex items-start justify-between p-4 pb-2">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={post.authorAvatar} />
              <AvatarFallback className="bg-primary/10 text-primary">
                {getInitials(post.authorName)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-sm font-semibold text-foreground">{post.authorName}</p>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Globe className="h-3 w-3" />
                <span>{formatDistanceToNow(new Date(post.createdAt), { addSuffix: true })}</span>
                {post.edited && <span>(edited)</span>}
              </div>
            </div>
          </div>

          {isOwn && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowMenu((value) => !value)}
                className="rounded-full p-1 text-muted-foreground hover:bg-muted"
                aria-label="Post options"
              >
                <MoreHorizontal className="h-5 w-5" />
              </button>
              {showMenu && (
                <div className="absolute right-0 top-8 z-20 min-w-36 rounded-lg border border-border bg-popover py-1 shadow-lg">
                  <button
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted"
                    onClick={() => {
                      setEditMode(true);
                      setShowMenu(false);
                    }}
                  >
                    <Pencil className="h-4 w-4" /> Edit post
                  </button>
                  <button
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-destructive hover:bg-destructive/10 dark:hover:bg-destructive/10"
                    onClick={handleDelete}
                  >
                    <Trash2 className="h-4 w-4" /> Delete post
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {post.sourceType && post.sourceType !== 'manual' && post.sourceEntityTitle && (
          <div className="px-4 pb-1">
            <span className="rounded-full border border-chart-4/40 bg-chart-4/10 px-2 py-0.5 text-xs text-chart-4">
              From {post.sourceType === 'task_comment' ? 'task' : post.sourceType === 'epic_comment' ? 'epic' : 'project'}: {post.sourceEntityTitle}
            </span>
          </div>
        )}

        <div className="px-4 pb-3">
          {editMode ? (
            <div className="space-y-2">
              <Textarea
                value={editContent}
                onChange={(event) => setEditContent(event.target.value)}
                className="min-h-[80px] bg-background text-sm"
              />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setEditMode(false)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleEdit}>
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <p className="whitespace-pre-wrap text-sm text-foreground/90">{post.content}</p>
          )}
        </div>

        {hasPhotos && (
          <div
            className={`grid gap-2 px-4 pb-3 ${
              post.photos!.length === 1
                ? 'grid-cols-1 max-w-xs'
                : post.photos!.length === 2
                  ? 'grid-cols-2 max-w-md'
                  : 'grid-cols-3 max-w-lg'
            }`}
          >
            {post.photos!.map((src, index) => (
              <button
                key={index}
                onClick={() => setExpandedImage(src)}
                className="group relative cursor-pointer overflow-hidden rounded-lg transition-all hover:ring-2 hover:ring-primary/40"
                aria-label={`View photo ${index + 1}`}
              >
                <img
                  src={src}
                  alt={`Photo ${index + 1}`}
                  className="h-20 w-full rounded-lg object-cover transition-opacity group-hover:opacity-80"
                />
                <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 transition-colors group-hover:bg-black/20">
                  <ZoomIn className="h-5 w-5 text-white opacity-0 transition-opacity group-hover:opacity-100" />
                </div>
              </button>
            ))}
          </div>
        )}

        {((post.reactions && post.reactions.length > 0) || (post.postComments && post.postComments.length > 0)) && (
          <div className="flex items-center justify-between border-border px-4 pb-1 text-xs text-muted-foreground">
            <div>
              {post.reactions && post.reactions.length > 0 && (
                <span>
                  {post.reactions.length} reaction{post.reactions.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            {post.postComments && post.postComments.length > 0 && (
              <button
                onClick={() => setShowComments((value) => !value)}
                className="hover:underline"
                aria-label="Toggle comments"
              >
                {post.postComments.length} comment{post.postComments.length !== 1 ? 's' : ''}
              </button>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 border-border px-4 py-1">
          <ReactionBar
            reactions={post.reactions || []}
            currentUserId={currentUserId}
            onReact={(emoji) => onReact(post, emoji)}
          />
          <button
            className="flex items-center gap-1 rounded-full px-3 py-1 text-sm text-muted-foreground hover:bg-muted"
            onClick={() => setShowComments((value) => !value)}
          >
            <MessageCircle className="h-4 w-4" />
            Comment
          </button>
        </div>

        {showComments && (
          <div className="space-y-1 px-4 py-3">
            {(post.postComments || []).map((comment) => (
              <FeedComment key={comment.id} comment={comment} />
            ))}
            <div className="mt-3 flex gap-2">
              <Avatar className="h-7 w-7 shrink-0">
                <AvatarImage src={currentUserAvatar} />
                <AvatarFallback className="bg-primary/10 text-xs text-primary">
                  {getInitials(currentUserName)}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-1 items-end gap-2">
                <ExpandableFeedTextarea
                  value={commentInput}
                  onValueChange={setCommentInput}
                  expanded={commentExpanded}
                  onExpandedChange={setCommentExpanded}
                  expandedRows={2}
                  expandedClassName="!min-h-20"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      void handleComment();
                    }
                  }}
                  placeholder="Write a comment..."
                  className="flex-1 rounded-2xl bg-background px-4"
                />
                <button
                  onClick={() => void handleComment()}
                  disabled={!commentInput.trim()}
                  className="text-primary hover:text-primary/90 disabled:opacity-40"
                  aria-label="Post comment"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {expandedImage && <ImageLightbox imageUrl={expandedImage} onClose={() => setExpandedImage(null)} />}
      </CardContent>
    </Card>
  );
}

export function NewsFeedComposer({
  currentUserName,
  currentUserAvatar,
  fallbackAvatarSrc,
  placeholder,
  onSubmit,
  successMessage = 'Post shared!',
  errorMessage = 'Failed to share post',
}: NewsFeedComposerProps) {
  const [content, setContent] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [posting, setPosting] = useState(false);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const composerExpanded = isExpanded || Boolean(content.trim()) || photos.length > 0;

  const handlePhotoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      setIsExpanded(true);
    }

    files.forEach((file) => {
      if (!file.type.startsWith('image/')) {
        return;
      }

      const reader = new FileReader();
      reader.onload = (loadEvent) => {
        setPhotos((currentPhotos) => [...currentPhotos, loadEvent.target?.result as string]);
      };
      reader.readAsDataURL(file);
    });

    if (event.target) {
      event.target.value = '';
    }
  };

  const handlePost = async () => {
    if (!content.trim() && photos.length === 0) {
      return;
    }

    setPosting(true);

    try {
      await onSubmit({
        content: content.trim(),
        photos,
      });
      setContent('');
      setPhotos([]);
      setIsExpanded(false);
      toast.success(successMessage);
    } catch (error) {
      console.error('[NewsFeedComposer] Failed to post:', error);
      toast.error(errorMessage);
    } finally {
      setPosting(false);
    }
  };

  return (
    <Card className="border-border bg-card">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10 shrink-0">
            <AvatarImage src={currentUserAvatar || fallbackAvatarSrc} />
            <AvatarFallback className="bg-primary/10 text-primary">
              {getInitials(currentUserName)}
            </AvatarFallback>
          </Avatar>
          <ExpandableFeedTextarea
            value={content}
            onValueChange={setContent}
            expanded={composerExpanded}
            onExpandedChange={setIsExpanded}
            persistExpanded={photos.length > 0}
            placeholder={placeholder}
            className="flex-1 rounded-2xl bg-background text-sm"
          />
        </div>

        {photos.length > 0 && (
          <div className="grid grid-cols-4 gap-2">
            {photos.map((src, index) => (
              <div key={index} className="group relative">
                <button
                  onClick={() => setExpandedImage(src)}
                  className="relative w-full cursor-pointer overflow-hidden rounded-lg transition-all hover:ring-2 hover:ring-primary/40"
                  aria-label={`View photo ${index + 1}`}
                >
                  <img src={src} alt="" className="h-16 w-full rounded-lg object-cover transition-opacity group-hover:opacity-80" />
                  <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 transition-colors group-hover:bg-black/20">
                    <ZoomIn className="h-4 w-4 text-white opacity-0 transition-opacity group-hover:opacity-100" />
                  </div>
                </button>
                <button
                  onClick={() => setPhotos((currentPhotos) => currentPhotos.filter((_, photoIndex) => photoIndex !== index))}
                  className="absolute right-1 top-1 rounded-full bg-destructive/10 p-1 text-white opacity-0 transition hover:bg-destructive/10 group-hover:opacity-100"
                  aria-label="Remove photo"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between border-t border-border pt-1">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-primary transition-colors hover:bg-primary/10"
          >
            <Image className="h-4 w-4" />
            Photo / Video
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handlePhotoSelect}
            aria-label="Upload photos"
          />
          <Button
            size="sm"
            onClick={() => void handlePost()}
            disabled={posting || (!content.trim() && photos.length === 0)}
            className="rounded-full px-5"
          >
            <Send className="mr-2 h-4 w-4" />
            Post
          </Button>
        </div>

        {expandedImage && <ImageLightbox imageUrl={expandedImage} onClose={() => setExpandedImage(null)} />}
      </CardContent>
    </Card>
  );
}